import { useEffect, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "../styles/BlogPost.css";

// ── Audio embed (Spotify) ────────────────────────────────────────────────

function AudioEmbed({ src, title, label, badge, caption, floatingName }) {
  const audioRef = useRef(null);
  const [isFloating, setIsFloating] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!audioRef.current) return;

      const rect = audioRef.current.getBoundingClientRect();

      // Once the player has mostly scrolled off screen,
      // turn it into a small floating player.
      if (rect.bottom < 80 && isPlaying) {
        setIsFloating(true);
      } else if (rect.top > window.innerHeight - 100) {
        setIsFloating(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isPlaying]);

  return (
    <>
      {/* Main player location inside the article */}
      <div
        ref={audioRef}
        className={`blog-video-container ${
          isFloating ? "video-placeholder" : ""
        }`}
      >
        <div className="blog-video-card">
          <div className="blog-video-header">
            <div>
              <span className="blog-video-label">{label}</span>
              <h2>{title}</h2>
            </div>

            <span className="blog-video-duration">{badge}</span>
          </div>

          <div className="blog-audio-wrap">
            <iframe
              className="blog-audio-frame"
              src={src}
              title={title}
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              onLoad={() => setIsPlaying(true)}
            />
          </div>

          <div className="blog-video-caption">
            <span>▶</span>
            {caption}
          </div>
        </div>
      </div>

      {/* Floating player */}
      {isFloating && (
        <div className="floating-video floating-audio">
          <div className="floating-video-header">
            <span>{floatingName}</span>

            <button
              className="floating-video-close"
              onClick={() => setIsFloating(false)}
              aria-label="Close floating player"
            >
              ×
            </button>
          </div>

          <iframe
            src={src}
            title={title}
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
          />
        </div>
      )}
    </>
  );
}

// ── Platform logo ────────────────────────────────────────────────────────
// Uses the official logo files from each platform's brand kit.
// Save them in /public/logos/ as youtube.svg, spotify.svg, apple-podcasts.svg.
// If a file is missing, the image simply hides itself.

const PLATFORM_LOGOS = {
  youtube: { src: "/logos/youtube.svg", name: "YouTube" },
  spotify: { src: "/logos/spotify.svg", name: "Spotify" },
  apple: { src: "/logos/apple-podcasts.svg", name: "Apple Podcasts" },
};

function PlatformLogo({ platform, size = 20 }) {
  const { src, name } = PLATFORM_LOGOS[platform];

  return (
    <img
      src={src}
      alt={`${name} logo`}
      width={size}
      height={size}
      style={{
        display: "inline-block",
        verticalAlign: "middle",
        marginRight: "0.4em",
        objectFit: "contain",
      }}
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
    />
  );
}

// ── Heart graphic ────────────────────────────────────────────────────────

function HeartGraphic() {
  return (
    <div
      className="blog-heart"
      style={{ textAlign: "center", margin: "2.5rem 0 1rem" }}
      aria-hidden="true"
    >
      <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="#e0245e"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
      </svg>
    </div>
  );
}

// ── Blog post ───────────────────────────────────────────────────────────

function BlogPost16() {
  return (
    <>
      <Navbar />

      <article className="blog-post">

        {/* ── Breadcrumb ── */}
        <p className="blog-breadcrumb">
          <span>Podcast & Personal</span>
          <span className="breadcrumb-divider">|</span>
          <span className="breadcrumb-section">
            Tennis Life Lessons | A Year of Documenting the Journey
          </span>
        </p>

        {/* ── Headline & dek ── */}
        <h1 className="blog-headline">
          A Year of Experimenting, and What Comes Next: Introducing the
          Tennis Life Lessons Podcast
        </h1>

        <p className="blog-dek">
          From News, to Cyber, to Tennis: a year of getting stuck,
          pivoting, and learning a SH*T-TON, and the new podcast my dad
          and I are starting to keep the journey going.
        </p>

        {/* ── Share row ── */}
        <div className="blog-meta-row">
          <button
            className="blog-share-btn"
            onClick={() =>
              navigator.share?.({
                title: document.title,
                url: window.location.href,
              })
            }
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            Share
          </button>
        </div>

        {/* ── Date + read time ── */}
        <p className="blog-dateline">
          October 13, 2026 &nbsp;|&nbsp; 3 min read
        </p>

        {/* ── Embedded player ── */}
        <AudioEmbed
          src="https://open.spotify.com/embed/show/1nZm0JnLMR0ARr12zgfiMR?utm_source=generator"
          title="Tennis Life Lessons"
          label="TENNIS LIFE LESSONS"
          badge="Now Streaming"
          caption="Listen on Spotify — also on Apple Podcasts and YouTube."
          floatingName="Tennis Life Lessons"
        />

        {/* ── Body copy ── */}
        <div className="blog-body">

          <p className="blog-drop-cap">
            The past year I've been in experimentation mode.
          </p>

          <p>
            <strong>First it was News.</strong> How can we inform more
            millennials, Gen Z, and Gen Alpha about what is going on in
            the world?
          </p>

          <p>
            <strong>Second, it was Cyber.</strong> How can we show
            people how to do cool technical things both with and without
            AI?
          </p>

          <p>
            <strong>Third, and most recently, it's been Tennis.</strong>{" "}
            How can I document the ultimate experience of being a tennis
            player, but, more importantly, relate it back to life?
          </p>

          <p>
            Ultimately, it's been a bunch of cycles. It's been getting
            stuck. Pivoting because something isn't working. Jumping up
            and down when something finally went a little bit viral.
            Being embarrassed because my friends might now think of me
            as a "Wannabe-Influencer."
          </p>

          <p>
            But, to be honest, after a year of this all, I've learned a
            lot. A SH*T-TON actually (sorry Grandpa, I know you're
            reading this and probably don't like the usage of that curse
            word).
          </p>

          <p>
            I'm really thankful for this past year of documentation. I'm
            really thankful for the people that have been involved in
            helping me (you included).
          </p>

          <p>
            Anyways, all of that goes to say I'm excited for another
            year of documenting the journey and seeing where it leads.
            And I'm starting to learn a new skill (along with my dad).
            It's the art (and science) of{" "}
            <strong>podcasting</strong>.
          </p>

          <p>
            My dad and I started <strong>"Tennis Life Lessons."</strong>{" "}
            The goal of this podcast is to:
          </p>

          <ol>
            <li>
              Relate the lessons we learn through sports back to life
            </li>
            <li>
              Provide you applicable skills to improve your performance
              (both athletically or professionally)
            </li>
            <li>Document the journey</li>
          </ol>

          <p>
            It's now available on <PlatformLogo platform="apple" size={18} />
            Apple Podcasts, <PlatformLogo platform="spotify" size={18} />
            Spotify, and <PlatformLogo platform="youtube" size={18} />
            YouTube. Here are all the links:
          </p>

          <ul>
            <li>
              <PlatformLogo platform="youtube" />
              <strong>YouTube:</strong>{" "}
              <a
                href="https://www.youtube.com/@tennislifelessons"
                target="_blank"
                rel="noopener noreferrer"
              >
                youtube.com/@tennislifelessons
              </a>
            </li>
            <li>
              <PlatformLogo platform="spotify" />
              <strong>Spotify:</strong>{" "}
              <a
                href="https://open.spotify.com/show/1nZm0JnLMR0ARr12zgfiMR?si=LwJTufXFStyUzaH-SE6URA"
                target="_blank"
                rel="noopener noreferrer"
              >
                Tennis Life Lessons on Spotify
              </a>
            </li>
            <li>
              <PlatformLogo platform="apple" />
              <strong>Apple Podcasts:</strong> Search "Tennis Life
              Lessons"
            </li>
          </ul>

          <p>
            If you check it out or want to support, please consider
            joining the <strong>"Life Lessons Classroom"</strong> by
            following the podcast, leaving a review, liking, and/or
            subscribing on YouTube. I know it sounds cheesy and every
            "Influencer" tells you to do it, but the likes, follows,
            etc. do really go a long way. Those little pushes of buttons
            show that what is being created is worth documenting. In
            advance, thank you so much.
          </p>

        </div>

        <div className="blog-body">

          <p className="blog-takeaway">
            <strong>Takeaway:</strong> If you've made it to the bottom
            of this blog post (that now reads more like a letter), I
            know you've been dedicated to helping me learn new things
            and grow. If there's a way I can help you with something you
            are working on, please reach out at{" "}
            <a href="mailto:alexisbernthal4@gmail.com">
              alexisbernthal4@gmail.com
            </a>{" "}
            and reference this blog post / letter as the catalyst for
            outreach. We can only get better when we put ourselves out
            there and lift one another up.
          </p>

        </div>

        {/* ── Closing heart ── */}
        <HeartGraphic />

      </article>

      <Footer />
    </>
  );
}

export default BlogPost16;